var _this = this;
/**
 * Creates a helper that listens to Oskari map clicks and feature clicks and matches the two to notify listeners
 * with coordinates that were clicked with features that are in that location on the layer that we are interested in.
 * If you want to open a custom popup on clicked map location with content about the features that were hit, this makes it easier for you.
 *
 * Usage:
 *
        const helper = createFeatureClickHelper();
        helper.addListener((coords, features) => {
            // do stuff -> user clicked the map at coords and there are features referenced in the second param in that location
        });

        Note! The click handler only cares about features on one layer. You need to set the layer that you are interested in by listening to layer changes on the map and calling:

                helper.setLayer("[layer id]");

 * @param {Oskari.sandbox} sandbox for listening to events. Defaults to Oskari.getSandbox()
 * @returns helper for detecting click location and vector features on that location
 */
export var createFeatureClickHelper = function createFeatureClickHelper() {
  var sandbox = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Oskari.getSandbox();
  var NAME = 'FeatureClickHelper_' + Oskari.getSeq('clickHelper').nextVal();
  var layerOfInterest;
  var listeners = [];
  var clickedFeatures = {
    waiting: false
  };
  var _handleFeaturesClicked = function handleFeaturesClicked() {
    var delayed = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var coords = clickedFeatures.coords,
      features = clickedFeatures.features,
      waiting = clickedFeatures.waiting;
    if (!coords || !features) {
      // we don't yet have one of the two things we need
      return;
    }
    if (waiting && !delayed) {
      // another call scheduled as a delayed call. Wait for it instead.
      return;
    }
    if (!delayed) {
      // make sure we don't have a saved click from another location if we get
      // the features first. Call itself again with a flag to identify a delayed call.
      clickedFeatures.waiting = true;
      setTimeout(function () {
        return _handleFeaturesClicked(true);
      }, 50);
      return;
    }
    // reset waiting if we get this far
    // -> called with delay so we can start expecting more delayed calls
    clickedFeatures.waiting = false;
    listeners.forEach(function (fn) {
      fn(coords, features);
    });
    delete clickedFeatures.coords;
    delete clickedFeatures.features;
  };
  var eventHandlers = {
    MapClickedEvent: function MapClickedEvent(event) {
      clickedFeatures.coords = event.getLonLat();
      _handleFeaturesClicked();
    },
    FeatureEvent: function FeatureEvent(event) {
      if (event.getOperation() !== 'click' || typeof layerOfInterest === 'undefined') {
        return;
      }
      var interestedInAllLayers = layerOfInterest === null;
      var features = event.getParams().features.filter(function (f) {
        return interestedInAllLayers || f.layerId === layerOfInterest;
      });
      if (!features.length) {
        delete clickedFeatures.coords;
        return;
      }
      clickedFeatures.features = features;
      _handleFeaturesClicked();
    }
  };
  var listenerModule = {
    getName: function getName() {
      return NAME;
    },
    onEvent: function onEvent(event) {
      var handler = eventHandlers[event.getName()];
      if (!handler) {
        return;
      }
      return handler.call(_this, event);
    }
  };
  Object.keys(eventHandlers).forEach(function (eventName) {
    return sandbox.registerForEventByName(listenerModule, eventName);
  });
  return {
    setLayer: function setLayer(id) {
      return layerOfInterest = id;
    },
    addListener: function addListener(fn) {
      return listeners.push(fn);
    },
    destroy: function destroy() {
      Object.keys(eventHandlers).forEach(function (eventName) {
        return sandbox.unregisterFromEventByName(_this, eventName);
      });
    }
  };
};